import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, Eye, Clock, Users, TrendingUp, Radio, RefreshCw, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/api';

const AdminAnalytics = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameDetail, setGameDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadOverview = useCallback(async () => {
    try {
      const res = await api.getAnalyticsOverview();
      setOverview(res.data);
    } catch (e) {
      console.error('Failed to load analytics:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const loadGameDetail = async (gameId) => {
    setSelectedGame(gameId);
    setLoadingDetail(true);
    try {
      const res = await api.getGameAnalytics(gameId);
      setGameDetail(res.data);
    } catch { setGameDetail(null); }
    finally { setLoadingDetail(false); }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-6 h-6 animate-spin text-sky-400" />
      </div>
    );
  }

  if (!overview) {
    return <div className="text-center py-16 text-gray-500">Failed to load analytics</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Streaming Analytics</h2>
          <p className="text-gray-400 text-sm mt-1">Viewership, engagement, and audience insights</p>
        </div>
        <Button variant="ghost" size="sm" onClick={loadOverview} className="text-gray-400 hover:text-white">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Total Views" value={overview.total_sessions} sub={`${overview.sessions_24h} today`} color="sky" />
        <StatCard icon={Users} label="Unique Viewers" value={overview.unique_viewers} sub={`${overview.unique_24h} today`} color="emerald" />
        <StatCard icon={Clock} label="Watch Hours" value={overview.total_watch_hours} sub={`${overview.watch_hours_24h}h today`} color="amber" />
        <StatCard icon={Radio} label="Live Now" value={overview.current_live_viewers} sub="current viewers" color="red" />
      </div>

      {/* Top Games */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-sky-400" /> Top Games (30 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {overview.top_games.length === 0 ? (
            <p className="text-gray-500 text-sm py-4">No view data yet. Views will appear as users watch games.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="top-games-table">
                <thead>
                  <tr className="border-b border-gray-700 text-gray-400">
                    <th className="text-left py-2 pl-2">Game</th>
                    <th className="text-right pr-3">Views</th>
                    <th className="text-right pr-3">Unique</th>
                    <th className="text-right pr-3">Watch Hours</th>
                    <th className="text-right pr-3">Avg Time</th>
                    <th className="text-right pr-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {overview.top_games.map((game, i) => (
                    <tr key={game.game_id} className="border-b border-gray-800 hover:bg-gray-700/30 transition-colors">
                      <td className="py-2.5 pl-2">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500 text-xs font-mono w-4">{i + 1}</span>
                          <div>
                            <span className="text-white text-sm font-medium">{game.title}</span>
                            <div className="flex items-center gap-1 mt-0.5">
                              <Badge className={`text-xs ${game.status === 'live' ? 'bg-red-500' : 'bg-gray-600'} text-white`}>
                                {game.status || 'scheduled'}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="text-right pr-3 text-white font-medium">{game.views}</td>
                      <td className="text-right pr-3 text-gray-300">{game.unique_viewers}</td>
                      <td className="text-right pr-3 text-gray-300">{game.total_watch_hours}h</td>
                      <td className="text-right pr-3 text-gray-300">{game.avg_watch_minutes}m</td>
                      <td className="text-right pr-2">
                        <Button variant="ghost" size="sm" onClick={() => loadGameDetail(game.game_id)} className="text-sky-400 hover:text-sky-300 h-7 px-2" data-testid={`drill-${game.game_id}`}>
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Game Detail Drill-Down */}
      {selectedGame && (
        <Card className="bg-gray-800/50 border-gray-700" data-testid="game-detail-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white">
                {loadingDetail ? 'Loading...' : gameDetail?.title || 'Game Details'}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setSelectedGame(null)} className="text-gray-400 hover:text-white text-xs">
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingDetail ? (
              <div className="py-4 text-center"><RefreshCw className="w-5 h-5 animate-spin text-sky-400 mx-auto" /></div>
            ) : gameDetail ? (
              <div className="space-y-4">
                {/* Stats Row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <MiniStat label="Total Views" value={gameDetail.total_views} />
                  <MiniStat label="Unique Viewers" value={gameDetail.unique_viewers} />
                  <MiniStat label="Live Views" value={gameDetail.live_views} />
                  <MiniStat label="Replay Views" value={gameDetail.replay_views} />
                  <MiniStat label="Peak Concurrent" value={gameDetail.peak_concurrent} />
                  <MiniStat label="Watch Hours" value={`${gameDetail.total_watch_hours}h`} />
                  <MiniStat label="Avg Watch" value={`${gameDetail.avg_watch_minutes}m`} />
                  <MiniStat label="Longest Session" value={`${gameDetail.longest_session_minutes}m`} />
                </div>

                {/* Viewer Table */}
                {gameDetail.viewers?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-2">Viewers ({gameDetail.viewers.length})</h4>
                    <div className="overflow-x-auto max-h-64 overflow-y-auto">
                      <table className="w-full text-sm" data-testid="viewer-table">
                        <thead className="sticky top-0 bg-gray-800">
                          <tr className="text-gray-400 border-b border-gray-700">
                            <th className="text-left py-1.5 pl-2">Viewer</th>
                            <th className="text-right pr-3">Sessions</th>
                            <th className="text-right pr-3">Watch Time</th>
                            <th className="text-right pr-2">Last Watched</th>
                          </tr>
                        </thead>
                        <tbody>
                          {gameDetail.viewers.map(v => (
                            <tr key={v._id} className="border-b border-gray-800/50">
                              <td className="py-1.5 pl-2">
                                <span className="text-white text-xs">{v.name || 'Anonymous'}</span>
                                <span className="text-gray-500 text-xs ml-2">{v.email}</span>
                              </td>
                              <td className="text-right pr-3 text-gray-300 text-xs">{v.sessions}</td>
                              <td className="text-right pr-3 text-gray-300 text-xs">{formatDuration(v.total_duration)}</td>
                              <td className="text-right pr-2 text-gray-500 text-xs">
                                {v.last_watch ? new Date(v.last_watch).toLocaleDateString() : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No analytics data for this game</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      {overview.recent_activity?.length > 0 && (
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-400" /> Recent Viewer Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {overview.recent_activity.map((s, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-700/30 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-gray-700 flex items-center justify-center text-xs text-white font-bold">
                      {(s.viewer_name || '?')[0]}
                    </div>
                    <div>
                      <span className="text-white text-xs">{s.viewer_name || 'Anonymous'}</span>
                      <span className="text-gray-500 text-xs ml-2">watched</span>
                      <span className="text-sky-400 text-xs ml-1">{s.game_title}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-gray-400 text-xs">{formatDuration(s.duration_seconds)}</span>
                    <span className="text-gray-600 text-xs ml-2">
                      {s.is_live ? '(live)' : '(replay)'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Stat card component
const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <Card className="bg-gray-800/50 border-gray-700">
    <CardContent className="pt-4 pb-3">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-400 text-xs">{label}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          <p className="text-gray-500 text-xs mt-0.5">{sub}</p>
        </div>
        <div className={`p-2 rounded-lg bg-${color}-500/10`}>
          <Icon className={`w-5 h-5 text-${color}-400`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Mini stat for game detail
const MiniStat = ({ label, value }) => (
  <div className="bg-black/30 rounded-lg p-2.5 text-center">
    <p className="text-gray-500 text-xs">{label}</p>
    <p className="text-white font-bold text-lg mt-0.5">{value}</p>
  </div>
);

export default AdminAnalytics;
